import parse from 'jsonpath-rfc9535/parser';

export type FieldPathSegments = (string | number)[];

const MAX_PATH_LENGTH = 4096;
const MAX_PATH_SEGMENTS = 128;

export const parseFieldPath = (path: string): FieldPathSegments | undefined => {
  if (!path || path.length > MAX_PATH_LENGTH) {
    return undefined;
  }

  try {
    const rootedPath = path.startsWith('$')
      ? path
      : path.startsWith('[')
        ? `$${path}`
        : `$.${path}`;
    const { segments } = parse(rootedPath);
    if (segments.length > MAX_PATH_SEGMENTS) {
      return undefined;
    }

    const result: FieldPathSegments = [];
    for (const segment of segments) {
      if (segment.type !== 'ChildSegment') {
        return undefined;
      }

      const { node } = segment;
      if (node.type === 'MemberNameShorthand') {
        result.push(node.value);
      } else if (
        node.type === 'BracketedSelection' &&
        node.selectors.length === 1
      ) {
        const [selector] = node.selectors;
        if (selector.type === 'NameSelector') {
          result.push(selector.value);
        } else if (
          selector.type === 'IndexSelector' &&
          Number.isSafeInteger(selector.value) &&
          selector.value >= 0
        ) {
          result.push(selector.value);
        } else {
          return undefined;
        }
      } else {
        return undefined;
      }
    }

    return result;
  } catch {
    return undefined;
  }
};

export const stringifyFieldPath = (
  segments: readonly (string | number)[],
): string =>
  '$' +
  segments
    .map((segment) => {
      if (typeof segment === 'number') {
        if (!Number.isSafeInteger(segment) || segment < 0) {
          throw new RangeError(
            'Field path indexes must be nonnegative safe integers',
          );
        }
        return `[${segment}]`;
      }
      return `[${JSON.stringify(segment)}]`;
    })
    .join('');
